// Ruumi Application Entry Point
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import router from './router';
import i18n from './locales/i18n';
import App from './App.vue';

// Import PrimeVue components globally (required - do NOT import in individual components)
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import AutoComplete from 'primevue/autocomplete';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import Breadcrumb from 'primevue/breadcrumb';
import Button from 'primevue/button';
import Card from 'primevue/card';
import Checkbox from 'primevue/checkbox';
import ColorPicker from 'primevue/colorpicker';
import Column from 'primevue/column';
import ConfirmDialog from 'primevue/confirmdialog';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import Divider from 'primevue/divider';
import Drawer from 'primevue/drawer';
import FileUpload from 'primevue/fileupload';
import FloatLabel from 'primevue/floatlabel';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputMask from 'primevue/inputmask';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import Message from 'primevue/message';
import MultiSelect from 'primevue/multiselect';
import Paginator from 'primevue/paginator';
import Panel from 'primevue/panel';
import Password from 'primevue/password';
import ProgressBar from 'primevue/progressbar';
import ProgressSpinner from 'primevue/progressspinner';
import RadioButton from 'primevue/radiobutton';
import Select from 'primevue/select';
import SelectButton from 'primevue/selectbutton';
import Skeleton from 'primevue/skeleton';
import SplitButton from 'primevue/splitbutton';
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import Step from 'primevue/step';
import StepPanels from 'primevue/steppanels';
import StepPanel from 'primevue/steppanel';
import Tab from 'primevue/tab';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tag from 'primevue/tag';
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import ToggleButton from 'primevue/togglebutton';
import ToggleSwitch from 'primevue/toggleswitch';
import Toolbar from 'primevue/toolbar';
import Tooltip from 'primevue/tooltip';

// Import PrimeIcons
import 'primeicons/primeicons.css';

// Import Tailwind CSS
import './style.css';

// Import Ruumi Amber Theme CSS
import './assets/ruumi-theme.css';

// Create Vue app
const app = createApp(App);

// Configure PrimeVue with Aura theme (light mode only with amber primary color)
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: false,
      cssLayer: false
    }
  },
  pt: {
    global: {
      css: `
        /* Override PrimeVue default teal/green with amber globally */
        :root {
          --p-primary-50: #FFFBEB !important;
          --p-primary-100: #FEF3C7 !important;
          --p-primary-200: #FDE68A !important;
          --p-primary-300: #FCD34D !important;
          --p-primary-400: #FBBF24 !important;
          --p-primary-500: #F59E0B !important;
          --p-primary-600: #D97706 !important;
          --p-primary-700: #B45309 !important;
          --p-primary-800: #92400E !important;
          --p-primary-900: #78350F !important;
        }
        
        /* Ensure scrollbar is amber globally */
        * {
          scrollbar-color: #FBBF24 #FFFBEB;
        }
        
        /* All buttons - add border radius globally */
        .p-button:not(.p-button-icon-only) {
          border-radius: 0.5rem !important;
        }
        
        /* Dialog - rounded corners */
        .p-dialog,
        .p-dialog-content,
        .p-dialog .p-dialog-content {
          border-radius: 0.75rem !important;
          overflow: hidden !important;
        }
        
        .p-dialog-header {
          border-top-left-radius: 0.75rem !important;
          border-top-right-radius: 0.75rem !important;
          margin-bottom: 1em !important;
        }
        
        .p-dialog-footer {
          border-bottom-left-radius: 0.75rem !important;
          border-bottom-right-radius: 0.75rem !important;
        }
        
        /* Toast - white background with colored left border */
        .p-toast-message,
        .p-toast .p-toast-message,
        div.p-toast-message,
        .p-toast-message-content,
        .p-toast .p-toast-message-content {
          background: white !important;
          background-color: white !important;
        }
        
        .p-toast-message {
          border-radius: 0.5rem !important;
          overflow: hidden !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
        }
        
        .p-toast-message-success,
        .p-toast .p-toast-message-success,
        div.p-toast-message.p-toast-message-success {
          background: white !important;
          background-color: white !important;
          border: 1px solid #10B981 !important;
          border-left: 4px solid #10B981 !important;
        }
        
        .p-toast-message-info,
        .p-toast .p-toast-message-info,
        div.p-toast-message.p-toast-message-info {
          background: white !important;
          background-color: white !important;
          border: 1px solid #3B82F6 !important;
          border-left: 4px solid #3B82F6 !important;
        }
        
        .p-toast-message-warn,
        .p-toast .p-toast-message-warn,
        div.p-toast-message.p-toast-message-warn {
          background: white !important;
          background-color: white !important;
          border: 1px solid #F59E0B !important;
          border-left: 4px solid #F59E0B !important;
        }
        
        .p-toast-message-error,
        .p-toast .p-toast-message-error,
        div.p-toast-message.p-toast-message-error {
          background: white !important;
          background-color: white !important;
          border: 1px solid #EF4444 !important;
          border-left: 4px solid #EF4444 !important;
        }
        
        .p-toast-message .p-toast-message-content,
        .p-toast-message .p-toast-summary,
        .p-toast-message .p-toast-detail {
          color: #1F2937 !important;
        }
        
        .p-toast-message-success .p-toast-message-icon,
        .p-toast-message-success .p-icon {
          color: #10B981 !important;
        }
        
        .p-toast-message-info .p-toast-message-icon,
        .p-toast-message-info .p-icon {
          color: #3B82F6 !important;
        }
        
        .p-toast-message-warn .p-toast-message-icon,
        .p-toast-message-warn .p-icon {
          color: #F59E0B !important;
        }
        
        .p-toast-message-error .p-toast-message-icon,
        .p-toast-message-error .p-icon {
          color: #EF4444 !important;
        }
        
        /* Solid buttons - amber primary (default) */
        .p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-secondary):not(.p-button-danger):not(.p-button-success):not(.p-button-info):not(.p-button-warning):not(.p-button-help):not(.p-button-contrast) {
          background-color: #F59E0B !important;
          border-color: #F59E0B !important;
          color: white !important;
        }

                /* Solid secondary buttons - darker amber */
                .p-button.p-button-secondary:not(.p-button-outlined):not(.p-button-text) {
                  background-color: #B45309 !important; /* darker amber */
                  border-color: #B45309 !important;
                  color: white !important;
                }

                .p-button.p-button-secondary:not(.p-button-outlined):not(.p-button-text):enabled:hover {
                  background-color: #92400E !important; /* even darker amber */
                  border-color: #92400E !important;
                  color: white !important;
                }
        
        .p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-secondary):not(.p-button-danger):not(.p-button-success):not(.p-button-info):not(.p-button-warning):not(.p-button-help):not(.p-button-contrast):enabled:hover {
          background-color: #D97706 !important;
          border-color: #D97706 !important;
          color: white !important;
        }
        
        /* ALL outlined buttons - amber by default */
        .p-button.p-button-outlined:not(.p-button-secondary):not(.p-button-danger):not(.p-button-success) {
          border-color: #F59E0B !important;
          color: #F59E0B !important;
          background-color: transparent !important;
        }
        
        .p-button.p-button-outlined:not(.p-button-secondary):not(.p-button-danger):not(.p-button-success):enabled:hover {
          background-color: #FFFBEB !important;
          border-color: #D97706 !important;
          color: #D97706 !important;
        }
        
        /* Secondary outlined buttons (Cancel) */
        .p-button.p-button-outlined.p-button-secondary {
          border-color: #9CA3AF !important;
          color: #6B7280 !important;
          background-color: transparent !important;
        }
        
        .p-button.p-button-outlined.p-button-secondary:enabled:hover {
          background-color: #FFFBEB !important;
          border-color: #F59E0B !important;
          color: #F59E0B !important;
        }
        
        /* Text buttons - amber */
        .p-button.p-button-text:not(.p-button-secondary):not(.p-button-danger):not(.p-button-success) {
          color: #F59E0B !important;
          background-color: transparent !important;
        }
        
        .p-button.p-button-text:not(.p-button-secondary):not(.p-button-danger):not(.p-button-success):enabled:hover {
          background-color: #FFFBEB !important;
          color: #D97706 !important;
        }
        
        /* Secondary text button (Cancel) */
        .p-button.p-button-text.p-button-secondary {
          color: #6B7280 !important;
          background-color: transparent !important;
        }
        
        .p-button.p-button-text.p-button-secondary:enabled:hover {
          background-color: #F3F4F6 !important;
          color: #374151 !important;
        }
        
        /* Rounded text buttons - amber */
        .p-button.p-button-rounded.p-button-text:enabled:hover {
          background-color: #FFFBEB !important;
          color: #D97706 !important;
        }
        
        /* Dialog close button (top right X) - override all button styles */
        .p-dialog-close-button,
        .p-dialog-header-close,
        button.p-dialog-close-button {
          color: #6B7280 !important;
          background-color: transparent !important;
          border: 2px solid #9CA3AF !important;
          width: 2.5rem !important;
          height: 2.5rem !important;
          border-radius: 50% !important;
        }
        
        .p-dialog-close-button:enabled:hover,
        .p-dialog-header-close:enabled:hover,
        button.p-dialog-close-button:enabled:hover {
          background-color: #FFFBEB !important;
          border-color: #F59E0B !important;
          color: #F59E0B !important;
        }
        
        /* Override icon-only rounded button styles for dialog close */
        .p-button.p-button-icon-only.p-button-rounded.p-button-text.p-dialog-close-button {
          color: #6B7280 !important;
          background-color: transparent !important;
          border: 2px solid #9CA3AF !important;
        }
        
        .p-button.p-button-icon-only.p-button-rounded.p-button-text.p-dialog-close-button:enabled:hover {
          background-color: #FFFBEB !important;
          border-color: #F59E0B !important;
          color: #F59E0B !important;
        }
        
        /* Drawer close button specific styling */
        .p-drawer-header .p-button {
          background-color: rgba(255, 255, 255, 0.2) !important;
          color: white !important;
          border: none !important;
          margin-right: 1rem !important;
        }
        
        .p-drawer-header .p-button:enabled:hover {
          background-color: rgba(255, 255, 255, 0.3) !important;
          color: white !important;
        }
        
        /* Selected state for outlined buttons */
        .p-button.p-button-outlined.button-selected {
          background-color: #F59E0B !important;
          border-color: #F59E0B !important;
          color: white !important;
        }
        
        .p-button.p-button-outlined.button-selected:enabled:hover {
          background-color: #D97706 !important;
          border-color: #D97706 !important;
          color: white !important;
        }
        
        /* ConfirmDialog button spacing and padding */
        .p-confirmdialog .p-dialog-footer {
          padding: 1.25rem !important;
          gap: 0.75rem !important;
          display: flex !important;
          justify-content: flex-end !important;
        }
        
        .p-confirmdialog .p-dialog-footer .p-button {
          margin: 0 !important;
          padding: 0.625rem 1.25rem !important;
          min-width: 100px !important;
        }
        
        .p-confirmdialog .p-dialog-content {
          padding: 1.5rem !important;
        }
      `
    },
    button: {
      root: ({ props, context }: any) => ({
        class: [
          props.class,
          {
            'button-selected': context.selected || props.selected
          }
        ]
      })
    },
    dialog: {
      root: {
        class: 'rounded-lg'
      },
      mask: {
        class: 'bg-black/40'
      }
    },
    drawer: {
      mask: {
        class: 'bg-black/40'
      },
      header: {
        class: 'bg-gradient-to-r from-amber-600 to-amber-700 text-white'
      },
      content: {
        class: '!bg-white'
      },
      closeButton: {
        class: 'text-white hover:bg-amber-500/20'
      }
    }
  }
});

// Register PrimeVue components globally
app.component('Accordion', Accordion);
app.component('AccordionPanel', AccordionPanel);
app.component('AccordionHeader', AccordionHeader);
app.component('AccordionContent', AccordionContent);
app.component('AutoComplete', AutoComplete);
app.component('Avatar', Avatar);
app.component('Badge', Badge);
app.component('Breadcrumb', Breadcrumb);
app.component('Button', Button);
app.component('Card', Card);
app.component('Checkbox', Checkbox);
app.component('ColorPicker', ColorPicker);
app.component('Column', Column);
app.component('ConfirmDialog', ConfirmDialog);
app.component('DataTable', DataTable);
app.component('DatePicker', DatePicker);
app.component('Dialog', Dialog);
app.component('Divider', Divider);
app.component('Drawer', Drawer);
app.component('FileUpload', FileUpload);
app.component('FloatLabel', FloatLabel);
app.component('IconField', IconField);
app.component('InputIcon', InputIcon);
app.component('InputMask', InputMask);
app.component('InputNumber', InputNumber);
app.component('InputText', InputText);
app.component('Menu', Menu);
app.component('Message', Message);
app.component('MultiSelect', MultiSelect);
app.component('Paginator', Paginator);
app.component('Panel', Panel);
app.component('Password', Password);
app.component('ProgressBar', ProgressBar);
app.component('ProgressSpinner', ProgressSpinner);
app.component('RadioButton', RadioButton);
app.component('Select', Select);
app.component('SelectButton', SelectButton);
app.component('Skeleton', Skeleton);
app.component('SplitButton', SplitButton);
app.component('Stepper', Stepper);
app.component('StepList', StepList);
app.component('Step', Step);
app.component('StepPanels', StepPanels);
app.component('StepPanel', StepPanel);
app.component('Tab', Tab);
app.component('Tabs', Tabs);
app.component('TabList', TabList);
app.component('TabPanel', TabPanel);
app.component('TabPanels', TabPanels);
app.component('Tag', Tag);
app.component('Textarea', Textarea);
app.component('Toast', Toast);
app.component('ToggleButton', ToggleButton);
app.component('ToggleSwitch', ToggleSwitch);
app.component('Toolbar', Toolbar);

// Register PrimeVue directives
app.directive('tooltip', Tooltip);

// Add PrimeVue services
app.use(ToastService);
app.use(ConfirmationService);

// Add Pinia state management
app.use(createPinia());

// Add Vue Router
app.use(router);

// Add internationalization
app.use(i18n);

// Mount app
app.mount('#app');
